<template>
  <div class="chart-balance">
    <v-card class="chartWrapper mx-auto rounded-lg">
      <div class="chartInfo">
        <v-progress-circular
          v-if="isLoading"
          class="loading"
          indeterminate
          color="primary"
          :size="70"
          :width="7"
        ></v-progress-circular>
        <line-chart
          v-else
          :chartData="chartData"
          :options="options"
          :height="230"
        />
        <div class="supplement">
          <span class="thisMonth">{{ $t('common.common_this-month') }}</span>
          <span class="lastMonth">{{ $t('common.common_last-month') }}</span>
        </div>
      </div>
      <div class="dataInfo">
        <div class="periodSelect">
          <v-menu
            ref="menu"
            v-model="pickerMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">
                {{ $t('common.common_period') }}
                <span class="font-weight-bold ml-2">{{ formatedDate }}</span>
                <v-icon
                  color="#d8d8d8"
                  class="mt-n1 arrowIcon"
                  size="30"
                  :class="{ isOpen: pickerMenu }"
                  >mdi-menu-down</v-icon
                >
              </span>
            </template>
            <v-date-picker
              v-model="date"
              :active-picker.sync="activePicker"
              locale="jp-ja"
              :day-format="(date) => new Date(date).getDate()"
              type="month"
              :max="new Date(Date.now()).toISOString().substr(0, 10)"
              min="1950-01-01"
              @change="changeData"
            ></v-date-picker>
          </v-menu>
        </div>
        <div class="sum">
          <div class="sales">
            <span class="label">{{ $t('common.common_total-sale') }}</span>
            <span class="value">{{ formatedCurrentMonthSaleAmount }}</span>
            <span class="grossProfit"
              >（
              {{
                $t('common.common_reference-gross-profit-price') +
                formatedGrossProfitAmount
              }}）</span
            >
          </div>
          <div class="order">
            <span class="label">{{ $t('common.common_order-total') }}</span>
            <span class="value">{{ formatedCurrentMonthOrderAmount }}</span>
          </div>
        </div>
      </div>
    </v-card>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import moment from 'moment'
import LineChart from './Chart/LineChart.js'
import { commonUtil, currencyUtil } from '@/util'

export default {
  components: {
    LineChart
  },
  data() {
    return {
      isLoading: true,
      pickerMenu: false,
      activePicker: null,
      date: moment().format('YYYY-MM-DD')
    }
  },
  async created() {
    if (!this.authority.isOutsideStaff) {
      await this.getData()
    }
  },
  computed: {
    ...mapGetters('BalanceOfPayment', ['getBalanceOfPaymentListFromState']),
    ...mapGetters('User', ['authority']),
    currentMonthData() {
      if (!this.getBalanceOfPaymentListFromState.length) {
        return null
      }
      return this.getBalanceOfPaymentListFromState[0]
    },
    lastMonthData() {
      if (!this.getBalanceOfPaymentListFromState.length) {
        return null
      }
      return this.getBalanceOfPaymentListFromState[1]
    },
    formatedDate() {
      return moment(this.date).format('YYYY年M月')
    },
    formatedGrossProfitAmount() {
      if (!this.currentMonthData) {
        return
      }
      const { grossProfitAmount } = this.currentMonthData
      if (!grossProfitAmount) return '-'
      const prefix = Number(grossProfitAmount) > 0 ? '+' : ''
      return prefix + this.priceFormat(grossProfitAmount)
    },
    formatedCurrentMonthOrderAmount() {
      if (!this.currentMonthData) {
        return
      }
      const { dailyOrderAmountList } = this.currentMonthData
      if (!dailyOrderAmountList) return '-'
      const monthlyOrderAmount = dailyOrderAmountList.reduce(
        (accumulator, currentValue) => {
          return accumulator + Number(currentValue.amount)
        },
        0
      )
      const prefix = Number(monthlyOrderAmount) > 0 ? '-' : ''
      return prefix + this.priceFormat(monthlyOrderAmount)
    },
    formatedCurrentMonthSaleAmount() {
      if (!this.currentMonthData) {
        return
      }
      const { dailySaleAmountList } = this.currentMonthData
      if (!dailySaleAmountList) return '-'
      const monthlySaleAmount = dailySaleAmountList.reduce(
        (accumulator, currentValue) => {
          return accumulator + Number(currentValue.amount)
        },
        0
      )
      const prefix = Number(monthlySaleAmount) > 0 ? '+' : ''
      return prefix + this.priceFormat(monthlySaleAmount)
    },

    chartData() {
      if (!this.currentMonthData || !this.lastMonthData) {
        return
      }

      const makeList = (list) => {
        // グラフを加算式に変換
        const result = []
        list.forEach((each, index) => {
          if (index === 0) {
            const data = { amount: each.amount, date: each.date }
            result.push(data)
          } else {
            const beforeAmount = result[index - 1].amount
            const currentAmount = Number(beforeAmount) + Number(each.amount)
            const data = { amount: currentAmount.toString(), date: each.date }
            result.push(data)
          }
        })
        return result
      }
      // 当月
      const currentMonthData = {
        dailyOrderAmountList: makeList(
          this.currentMonthData.dailyOrderAmountList
        ),
        dailySaleAmountList: makeList(this.currentMonthData.dailySaleAmountList)
      }
      const dailyOrderAmount = currentMonthData.dailyOrderAmountList.map(
        (item) => item.amount
      )
      const dailySaleAmount = currentMonthData.dailySaleAmountList.map(
        (item) => item.amount
      )
      const label = currentMonthData.dailyOrderAmountList.map((item) =>
        moment(item.date).format('M/D')
      )
      // 前月
      const lastMonthData = {
        dailyOrderAmountList: makeList(this.lastMonthData.dailyOrderAmountList),
        dailySaleAmountList: makeList(this.lastMonthData.dailySaleAmountList)
      }
      const dailyOrderAmountLast = lastMonthData.dailyOrderAmountList.map(
        (item) => item.amount
      )
      const dailySaleAmountLast = lastMonthData.dailySaleAmountList.map(
        (item) => item.amount
      )

      return {
        labels: label,
        datasets: [
          {
            data: dailySaleAmount,
            borderColor: '#ff8f00',
            borderWidth: 2.5,
            lineTension: 0,
            pointRadius: 0,
            fill: false
          },
          {
            data: dailySaleAmountLast,
            borderColor: 'rgba(255, 143, 0, 0.4)',
            borderWidth: 2.5,
            borderDash: [6, 5],
            lineTension: 0,
            pointRadius: 0,
            fill: false
          },
          {
            data: dailyOrderAmount,
            borderColor: '#1f77b8',
            borderWidth: 2.5,
            lineTension: 0,
            pointRadius: 0,
            fill: false
          },
          {
            data: dailyOrderAmountLast,
            borderColor: 'rgba(31, 119, 184, 0.4)',
            borderWidth: 2.5,
            borderDash: [6, 5],
            lineTension: 0,
            pointRadius: 0,
            fill: false
          }
        ]
      }
    },
    options() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: false
        },
        scales: {
          yAxes: [
            {
              gridLines: {
                drawBorder: false,
                color: '#efefef',
                zeroLineColor: '#efefef'
              },
              ticks: {
                beginAtZero: true,
                autoSkip: true,
                maxTicksLimit: 5,
                padding: 15,
                fontColor: '#aaaaaa',
                callback: function (label, index, labels) {
                  return commonUtil.formatPrice(Number(label)).toLocaleString()
                }
              }
            }
          ],
          xAxes: [
            {
              gridLines: {
                color: 'rgba(0, 0, 0, 0)'
              },
              ticks: {
                autoSkip: true,
                maxTicksLimit: 5,
                maxRotation: 0,
                minRotation: 0,
                fontColor: '#999999'
              }
            }
          ]
        }
      }
    }
  },
  methods: {
    ...mapActions('BalanceOfPayment', ['getBalanceOfPaymentListFromApi']),
    priceFormat(amount) {
      const { currencyId } = this.currentMonthData
      const unit = currencyUtil.getCurrencyUnitName(currencyId)
      return isNaN(amount)
        ? ''
        : Math.floor(Number(amount)).toLocaleString() + unit
    },
    async getData() {
      const lastMonth = moment(this.date).subtract(1, 'M').format('YYYY-MM-DD')
      const currentStart = moment(this.date).startOf('M').format('YYYY-MM-DD')
      const currentEnd = moment(this.date).endOf('M').format('YYYY-MM-DD')
      const lastStart = moment(lastMonth).startOf('M').format('YYYY-MM-DD')
      const lastEnd = moment(lastMonth).endOf('M').format('YYYY-MM-DD')
      const today = moment().format('YYYY-MM-DD')

      const params = {
        currentMonth: {
          startDate: currentStart,
          endDate: moment().diff(currentEnd) <= 0 ? today : currentEnd
        },
        lastMonth: {
          startDate: lastStart,
          endDate: lastEnd
        }
      }
      this.isLoading = true
      if (!this.authority.isOutsideStaff) {
        await this.getBalanceOfPaymentListFromApi(params)
      }
      this.isLoading = false
    },
    async changeData() {
      this.pickerMenu = false
      if (!this.authority.isOutsideStaff) {
        await this.getData()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.chart-balance {
  min-width: 1126px;
  max-width: 1126px;
}
.chartWrapper {
  display: flex;

  .chartInfo {
    position: relative;
    flex: 1;
    height: 300px;
    padding-left: 40px;
    padding-right: 40px;
    padding-top: 40px;
    .loading {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
    .supplement {
      position: absolute;
      top: 15px;
      right: 55px;
      color: #999999;
      font-size: 12px;
      .lastMonth {
        position: relative;
        display: inline-block;
        text-align: right;
        width: 65px;
        &:before {
          width: 16px;
          content: '';
          background-image: linear-gradient(
            to right,
            #999999,
            #999999 6px,
            transparent 3px,
            transparent 8px
          );
          background-size: 10px 2px;
          background-repeat: repeat-x;
          position: absolute;
          top: 8px;
          bottom: 0;
          right: 30px;
        }
      }
      .thisMonth {
        position: relative;
        display: inline-block;
        text-align: right;
        width: 65px;
        &:before {
          position: absolute;
          content: '';
          width: 14px;
          top: 8px;
          right: 30px;
          border-radius: 10px;
          border: 1px solid #999999;
        }
      }
    }
  }
  .dataInfo {
    width: 260px;
    display: flex;
    flex-direction: column;
    padding-top: 20px;
    .periodSelect {
      color: #999999;
      .arrowIcon {
        &.isOpen {
          transform: rotate(180deg);
        }
      }
    }
    .sum {
      flex: 1;
      display: flex;
      flex-direction: column;
      justify-content: space-around;
      margin-top: 20px;
      margin-bottom: 20px;
      .sales,
      .order {
        display: flex;
        flex-direction: column;
        padding-left: 20px;
        .label {
          position: relative;
          color: #666666;
          &:before {
            content: '';
            width: 14px;
            height: 14px;
            border-radius: 14px;
            position: absolute;
            left: -22px;
            top: 2px;
          }
        }
        .value {
          font-weight: bold;
          font-size: 20px;
        }
        .grossProfit {
          color: #666666;
          font-size: 12px;
        }
      }
      .sales {
        .label {
          &:before {
            background: #ff8f00;
          }
        }
      }
      .order {
        .label {
          &:before {
            background: #1f77b8;
          }
        }
      }
    }
  }
}
</style>
